import { Component, OnInit } from '@angular/core';
import {
  ShipmentGeofenceViewModel,
  ShipmentLocationViewModel,
} from '@components/shipment-route-list/shipment-route-map/shipment-route-map.component';
import {
  DEFAULT_RADIUS_METERS,
  ShipmentLocationResponse,
} from '@entities/shipment-location';
import { MasterdataService } from '@services/api/masterdata.service';
import { ShipmentService } from '@services/api/shipment.service';
import {
  ShipmentDeviceViewModel,
  ShipmentListTableComponent,
  ShipmentSettingLocationViewModel,
  ShipmentSettingRouteViewModel,
  ShipmentViewModel,
} from './shipment-list-table/shipment-list-table.component';
import { MatCardModule } from '@angular/material/card';
import {
  DeviceFormViewModel,
  EditedShipmentViewModel,
  OptionFormViewModel,
  RepeatType,
  RouteFormViewModel,
  ShipmentEditDialogComponent,
  ShipmentEditorDialogViewModel,
} from './shipment-edit-dialog/shipment-edit-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import {
  ShipmentAddRequest,
  ShipmentResponse,
  ShipmentSettingLocationResponse,
  ShipmentSettingResponse,
  ShipmentUpdateRequest,
} from '@entities/shipment';
import {
  AlertDialogComponent,
  DialogOption as AlertDialogOption,
} from '@components/shared/alert-dialog/alert-dialog.component';
import {
  DeviceSettingUpdateRequest,
  ShipmentDeviceSettingRequest,
} from '@entities/deviceSetting';
import { ShipmentDeviceSettingService } from '@services/api/shipment-device-setting.service';
import { convertToDeviceSettingRequest } from '@utils/device-setting.util';
import { ApiError } from '@errors/error';

export type DeviceSettingListViewModel = {
  /** デバイス名 */
  deviceName: string;
  /** シリアルナンバー */
  deviceId: string;
  /** デバイス種類ID */
  deviceTypeId: string;
  /** デバイス種類表示名 */
  deviceTypeDisplayName?: string;
  /** 最終通信日時 */
  lastCommunicationAt: string;
  /** ステータス */
  status: string;
  /** バッテリー */
  battery: number;
  /** デバイスグループ名 */
  deviceSettingGroupName: string;
  /** タグ名 */
  tagNameList: string[];
};

@Component({
  selector: 'app-shipment-list',
  standalone: true,
  imports: [MatCardModule, ShipmentListTableComponent],
  templateUrl: './shipment-list.component.html',
  styleUrl: './shipment-list.component.scss',
})
export class ShipmentListComponent implements OnInit {
  /** 配送設定一覧 */
  public shipmentList: ShipmentViewModel[] = [];
  /** 位置情報設定一覧 */
  public shipmentLocationList: ShipmentLocationViewModel[] = [];
  /** 新規追加・編集フォームに渡すデバイス設定一覧 */
  public deviceSettingList: DeviceSettingListViewModel[] = [];
  /** マスターデータ取得中 */
  public loadingMasterData = false;
  /** 全デバイス設定一覧 */
  public allDeviceSettingList: DeviceSettingListViewModel[] = [];

  constructor(
    private shipmentService: ShipmentService,
    private shipmentDeviceSettingService: ShipmentDeviceSettingService,
    private masterdataService: MasterdataService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    // 配送設定の一覧を取得する
    this.initShipmentList();

    // 位置情報設定一覧を取得する
    this.loadingMasterData = true;
    this.masterdataService
      .find(['shipment_location', 'device_setting'], true)
      .subscribe({
        next: (masterdata) => {
          const shipmentLocationList = masterdata[
            'shipmentLocation'
          ] as ShipmentLocationResponse[];

          shipmentLocationList.sort((a, b) => {
            return a.locationName.localeCompare(b.locationName, undefined, {
              numeric: true,
              ignorePunctuation: true,
            });
          });

          this.shipmentLocationList = shipmentLocationList.map(
            (shipmentLocationResponse) => {
              const geometry = shipmentLocationResponse.geometry;
              let geofence: ShipmentGeofenceViewModel | undefined;
              if (geometry?.geofenceType === 'polygon') {
                geofence = {
                  geofenceType: 'polygon',
                  polygonList: geometry.polygonList ?? [],
                };
              } else if (geometry?.geofenceType === 'circle') {
                geofence = {
                  geofenceType: 'circle',
                  circleRadius: geometry?.circleRadius || 0,
                  circleRadiusUnit: geometry?.circleRadiusUnit ?? 'm',
                  circleRadiusInMeters:
                    geometry.circleRadiusInMeters ?? DEFAULT_RADIUS_METERS,
                };
              }

              return {
                locationId: shipmentLocationResponse.locationId,
                locationName: shipmentLocationResponse.locationName,
                latitude: shipmentLocationResponse.latitude,
                longitude: shipmentLocationResponse.longitude,
                isUse: shipmentLocationResponse.isUse,
                postalCode: shipmentLocationResponse.postalCode,
                address: shipmentLocationResponse.address,
                geofence: geofence,
              };
            },
          );

          const allDeviceSettingList = masterdata[
            'deviceSetting'
          ] as DeviceSettingListViewModel[];
          allDeviceSettingList.sort((a, b) =>
            a.deviceName.localeCompare(b.deviceName),
          );
          this.allDeviceSettingList = allDeviceSettingList;

          this.loadingMasterData = false;
        },
      });
  }

  /**
   * 配送設定の追加・編集ダイアログを表示し、確定時に登録処理を実行する
   * @param shipment 編集対象の配送設定
   */
  public async openShipmentEditDialog(shipment?: ShipmentViewModel) {
    // 新規追加時の初期値
    const options: ShipmentEditorDialogViewModel = {
      updating: false,
      isReadOnly: false,
      deviceSettingList: this.allDeviceSettingList,
      shipmentList: this.shipmentList,
      routeList: [],
      locationList: this.shipmentLocationList,
    };

    if (shipment) {
      // 編集時の初期値
      const isReadOnly = !['Creating', 'Create', 'NotReady'].includes(
        shipment.status,
      );
      options.updating = true;
      options.isReadOnly = isReadOnly;
      options.shipmentId = shipment.shipmentId;

      /** オプション */
      const shipmentOption: OptionFormViewModel = {
        shipmentName: shipment.shipmentName,
        repeatType: this.normalizeRepeatType(shipment.frequency),
      };

      /** ルート */
      const shipmentRoute: RouteFormViewModel = {
        startLocationId: shipment.route.startLocation?.locationId ?? null,
        arrivalLocationId: shipment.route.arrivalLocation?.locationId ?? null,
      };

      /** デバイス */
      const deviceList: ShipmentDeviceViewModel[] = shipment.deviceList || [];
      const device: ShipmentDeviceViewModel = deviceList.length
        ? deviceList[0]
        : {
            deviceId: '',
            deviceName: '',
          };
      const deviceSetting = this.allDeviceSettingList.find(
        (item) => item.deviceId === device.deviceId,
      );

      // 編集不可時（すでに出発した場合）に該当デバイスがなければ、削除済みとする
      const deviceDeleted = isReadOnly && !deviceSetting;

      const shipmentDevice: DeviceFormViewModel = {
        deviceId: deviceDeleted ? 'deletedDevice' : device.deviceId,
        deviceName: deviceDeleted ? '削除済みデバイス' : device.deviceName,
        deviceType: deviceDeleted
          ? '-'
          : (deviceSetting?.deviceTypeDisplayName ?? ''),
        deviceSettingGroupName: deviceSetting?.deviceSettingGroupName,
        deleted: deviceDeleted,
      };

      options.originalDeviceSetting = {
        deviceId: shipmentDevice.deviceId,
        deviceName: shipmentDevice.deviceName,
        deviceTypeId: deviceDeleted ? '-' : (deviceSetting?.deviceTypeId ?? ''),
        deviceSettingGroupName: shipmentDevice.deviceSettingGroupName ?? '',
        lastCommunicationAt: '',
        status: '',
        battery: 0,
        tagNameList: [],
      };

      options.target = {
        shipmentOption,
        shipmentRoute,
        shipmentDevice,
      };
    }

    const editedShipment: EditedShipmentViewModel = await firstValueFrom(
      this.dialog
        .open(ShipmentEditDialogComponent, {
          width: '80%',
          height: '90%',
          minWidth: '500px',
          data: options,
        })
        .afterClosed(),
    );

    // ダイアログ外クリック等で閉じた場合は処理しない
    if (!editedShipment) {
      return;
    }

    const startLocationId = editedShipment.shipmentRoute.startLocationId;
    const arrivalLocationId = editedShipment.shipmentRoute.arrivalLocationId;
    const deviceExisted = !!editedShipment.shipmentDevice.deviceId;

    if (!startLocationId || !arrivalLocationId || !deviceExisted) {
      this.openShipmentInValidErrorDialog(options.updating);
      return;
    }

    const startLocation = this.getLocationById(startLocationId);
    const arrivalLocation = this.getLocationById(arrivalLocationId);

    const locationList =
      startLocation && arrivalLocation ? [startLocation, arrivalLocation] : [];

    const requestOptions = {
      shipmentName: editedShipment.shipmentOption.shipmentName,
      frequency: editedShipment.shipmentOption.repeatType,
      arrivalDatetime: shipment?.arrivalDatetime,
      locationList,
    };

    // 配送用のデバイス詳細設定を登録する
    const deviceSettingRequestList =
      editedShipment.shipmentDeviceSettingList?.map((shipmentDeviceSetting) => {
        const deviceSettingRequest = convertToDeviceSettingRequest(
          shipmentDeviceSetting,
        );
        deviceSettingRequest.deviceId = shipmentDeviceSetting.deviceId;
        deviceSettingRequest.deviceName =
          shipmentDeviceSetting.deviceName || '';
        return deviceSettingRequest;
      });

    if (options.updating && shipment?.shipmentId) {
      await this.updateShipment(
        shipment.shipmentId,
        editedShipment,
        requestOptions,
        deviceSettingRequestList,
      );
    } else {
      await this.addShipment(
        editedShipment,
        requestOptions,
        deviceSettingRequestList,
      );
    }

    // 配送設定一覧を再取得する
    this.initShipmentList();
  }

  /**
   * 選択された配送設定を削除する
   * @param selectedShipmentList 選択された配送設定リスト
   */
  public async deletedShipment(selectedShipmentList: ShipmentViewModel[]) {
    const options: AlertDialogOption = {
      type: 'error',
      title: 'Confirm',
      message: `選択した配送設定 ${selectedShipmentList.length} 件を削除します。よろしいですか？`,
      operation: '削除',
    };

    const result: boolean = await firstValueFrom(
      this.dialog
        .open(AlertDialogComponent, {
          width: '600px',
          data: options,
        })
        .afterClosed(),
    );

    if (!result) {
      return;
    }

    const selectedShipmentIdList = selectedShipmentList.map(
      (shipment) => shipment.shipmentId,
    );

    let deletedShipmentIdList: string[] = [];
    let skippedShipmentIdList: string[] = [];
    let showInProgressError = false;

    try {
      // 配送削除が失敗しても関連デバイス設定だけ消えてしまうのを防ぐため、
      // shipment delete -> device setting delete の順で直列実行する。
      await firstValueFrom(this.shipmentService.delete(selectedShipmentIdList));
      deletedShipmentIdList = selectedShipmentIdList;
    } catch (error) {
      if (error instanceof ApiError && error.response.status === 409) {
        // API は削除成功/失敗の配送IDを返す想定。
        const deletedList = error.response.error?.detail?.parameters
          ?.deleted_shipment_list as string[] | undefined;
        const skippedList = error.response.error?.detail?.parameters
          ?.skipped_shipment_list as string[] | undefined;

        deletedShipmentIdList = deletedList ?? [];
        skippedShipmentIdList = skippedList ?? [];

        // 念のため、選択された配送IDのみを後続処理対象にする。
        const selectedShipmentIdSet = new Set(selectedShipmentIdList);
        deletedShipmentIdList = deletedShipmentIdList.filter((shipmentId) =>
          selectedShipmentIdSet.has(shipmentId),
        );
        skippedShipmentIdList = skippedShipmentIdList.filter((shipmentId) =>
          selectedShipmentIdSet.has(shipmentId),
        );

        if (
          skippedShipmentIdList.length === 0 &&
          deletedShipmentIdList.length < selectedShipmentIdList.length
        ) {
          const deletedShipmentIdSet = new Set(deletedShipmentIdList);
          skippedShipmentIdList = selectedShipmentIdList.filter(
            (shipmentId) => !deletedShipmentIdSet.has(shipmentId),
          );
        }

        // 異常系レスポンスで一覧が欠けている場合は安全側に倒し、未削除扱いにする。
        if (
          deletedShipmentIdList.length === 0 &&
          skippedShipmentIdList.length === 0
        ) {
          skippedShipmentIdList = selectedShipmentIdList;
        }

        showInProgressError = skippedShipmentIdList.length > 0;
      } else {
        const alertDialogOptions: AlertDialogOption = {
          type: 'error',
          message: `配送設定削除に失敗しました。\nしばらくしてからもう一度お試しください。`,
        };
        this.dialog.open(AlertDialogComponent, {
          width: '600px',
          data: alertDialogOptions,
        });
        return;
      }
    }

    if (deletedShipmentIdList.length > 0) {
      try {
        // shipment delete で削除済みと判断できた配送だけ後続の関連設定削除を行う。
        await firstValueFrom(
          this.shipmentDeviceSettingService.delete(deletedShipmentIdList),
        );
      } catch {
        if (!showInProgressError) {
          const alertDialogOptions: AlertDialogOption = {
            type: 'error',
            message:
              '配送設定に紐づくデバイス設定の削除に失敗しました。\nしばらくしてからもう一度お試しください。',
          };
          this.dialog.open(AlertDialogComponent, {
            width: '600px',
            data: alertDialogOptions,
          });
        }
      }

      // 409 の部分成功時も、削除できた配送は一覧から消えるよう再取得する。
      this.initShipmentList();
    }

    if (showInProgressError) {
      const skippedCount = skippedShipmentIdList.length;
      const alertDialogOptions: AlertDialogOption = {
        type: 'error',
        message:
          `配送設定削除に失敗しました。\n` +
          `${selectedShipmentIdList.length}件中${skippedCount}件が配送中のため削除できませんでした。\n` +
          `配送を中断してからもう一度お試しください。`,
      };
      this.dialog.open(AlertDialogComponent, {
        width: '600px',
        data: alertDialogOptions,
      });
    }
  }

  /**
   * 配送設定一覧を取得し、画面表示用のViewModelに変換する
   */
  public initShipmentList() {
    this.shipmentService.findSettingList().subscribe({
      next: (shipmentList) => {
        this.shipmentList = shipmentList.map((shipment) => ({
          shipmentId: shipment.shipmentId,
          shipmentName: shipment.shipmentName,
          frequency: this.normalizeRepeatType(shipment.frequency),
          status: shipment.status || '',
          startDatetime: '',
          updatedAt: shipment.updatedAt,
          deviceList: shipment.deviceList,
          route: this.createRouteFromSetting(shipment),
        }));
      },
    });
  }

  private createRouteFromSetting(
    shipment: ShipmentSettingResponse,
  ): ShipmentSettingRouteViewModel {
    return {
      startLocation: this.createLocationFromSetting(shipment.departure),
      arrivalLocation: this.createLocationFromSetting(shipment.destination),
    };
  }

  private createLocationFromSetting(
    location?: ShipmentSettingLocationResponse,
  ): ShipmentSettingLocationViewModel | undefined {
    if (!location) {
      return undefined;
    }

    return {
      locationId: location.locationId,
      locationName: location.locationName,
    };
  }

  private normalizeRepeatType(
    frequency: ShipmentSettingResponse['frequency'],
  ): RepeatType {
    if (frequency === 'once' || frequency === 'repetition') {
      return frequency;
    }
    return '';
  }

  private getLocationById(
    locationId: string,
  ): ShipmentLocationViewModel | undefined {
    return this.shipmentLocationList.find(
      (location) => location.locationId === locationId,
    );
  }

  private async addShipment(
    editedShipment: EditedShipmentViewModel,
    options: {
      shipmentName: string;
      frequency: '' | 'once' | 'repetition';
      arrivalDatetime?: string;
      locationList: ShipmentLocationViewModel[];
    },
    deviceSettingRequestList: DeviceSettingUpdateRequest[] = [],
  ): Promise<void> {
    // シップメント追加処理
    const addedShipmentRequest: ShipmentAddRequest = {
      shipmentName: options.shipmentName,
      frequency: options.frequency,
      route: {
        locationList: options.locationList,
      },
      deviceList: [
        {
          deviceId: editedShipment.shipmentDevice.deviceId,
          deviceName: editedShipment.shipmentDevice.deviceName,
        },
      ],
    };

    let addedShipment: ShipmentResponse;
    try {
      addedShipment = await firstValueFrom(
        this.shipmentService.add(addedShipmentRequest),
      );
    } catch (error) {
      if (
        error instanceof ApiError &&
        error.response.error.detail.type == 'AlreadyExistsWebException'
      ) {
        const alertDialogOptions: AlertDialogOption = {
          type: 'error',
          message: '既存データと重複しています。',
        };
        this.dialog.open(AlertDialogComponent, {
          width: '600px',
          data: alertDialogOptions,
        });
      } else {
        this.openShipmentErrorDialog(false);
      }
      return;
    }

    const shipmentDeviceList: ShipmentDeviceSettingRequest[] = [];
    for (const deviceSettingRequest of deviceSettingRequestList) {
      shipmentDeviceList.push({
        shipmentId: addedShipment.shipmentId,
        deviceSetting: deviceSettingRequest,
      });
    }

    if (shipmentDeviceList.length === 0) {
      return;
    }

    try {
      await firstValueFrom(
        this.shipmentDeviceSettingService.add(shipmentDeviceList),
      );
    } catch {
      const alertDialogOptions: AlertDialogOption = {
        type: 'error',
        message: 'デバイス設定の追加に失敗しました。',
      };
      this.dialog.open(AlertDialogComponent, {
        width: '600px',
        data: alertDialogOptions,
      });
    }
  }

  private async updateShipment(
    shipmentId: string,
    editedShipment: EditedShipmentViewModel,
    options: {
      shipmentName: string;
      frequency: '' | 'once' | 'repetition';
      arrivalDatetime?: string;
      locationList: ShipmentLocationViewModel[];
    },
    deviceSettingRequestList: DeviceSettingUpdateRequest[] = [],
  ): Promise<void> {
    // シップメント更新処理
    const updatedShipmentRequest: ShipmentUpdateRequest = {
      shipmentId: shipmentId,
      shipmentName: options.shipmentName,
      frequency: options.frequency,
      arrivalDatetime: options.arrivalDatetime,
      route: {
        locationList: options.locationList,
      },
      deviceList: [
        {
          deviceId: editedShipment.shipmentDevice.deviceId,
          deviceName: editedShipment.shipmentDevice.deviceName,
        },
      ],
    };

    let updatedShipment;
    try {
      updatedShipment = await firstValueFrom(
        this.shipmentService.update(
          updatedShipmentRequest.shipmentId,
          updatedShipmentRequest,
        ),
      );
    } catch (error) {
      if (
        error instanceof ApiError &&
        error.response.error.detail.type == 'AlreadyExistsWebException'
      ) {
        const alertDialogOptions: AlertDialogOption = {
          type: 'error',
          message: '既存データと重複しています。',
        };
        this.dialog.open(AlertDialogComponent, {
          width: '600px',
          data: alertDialogOptions,
        });
      } else {
        this.openShipmentErrorDialog(true);
      }
      return;
    }

    try {
      await firstValueFrom(
        this.shipmentDeviceSettingService.update(
          updatedShipment.shipmentId,
          deviceSettingRequestList,
        ),
      );
    } catch {
      const alertDialogOptions: AlertDialogOption = {
        type: 'error',
        message: 'デバイス設定の更新に失敗しました。',
      };
      this.dialog.open(AlertDialogComponent, {
        width: '600px',
        data: alertDialogOptions,
      });
    }
  }

  /**
   * 入力不足時のエラーダイアログを表示する
   * @param updating 更新処理中かどうか
   */
  private openShipmentInValidErrorDialog(updating: boolean) {
    const operation = updating ? '更新' : '追加';
    const alertDialogOptions: AlertDialogOption = {
      type: 'error',
      message: `${operation}に必要な入力が不足しています。`,
    };
    this.dialog.open(AlertDialogComponent, {
      width: '600px',
      data: alertDialogOptions,
    });
  }

  /**
   * 配送設定の追加・更新失敗時のエラーダイアログを表示する
   * @param updating 更新処理中かどうか
   */
  private openShipmentErrorDialog(updating: boolean) {
    const operation = updating ? '更新' : '追加';
    const alertDialogOptions: AlertDialogOption = {
      type: 'error',
      message: `配送設定の${operation}に失敗しました。`,
    };
    this.dialog.open(AlertDialogComponent, {
      width: '600px',
      data: alertDialogOptions,
    });
  }
}
